import React from 'react'
import { ActivityIndicator, FlatList, Text, TouchableOpacity, View } from 'react-native'
import { useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../../../lib/api'
import { ScreenHeader } from '../../../components/ui/ScreenHeader'
import { BotAvatar } from '../../../components/bots/BotAvatar'
import { formatBotCardStats, mergeBotStats, type BotStatsResponse } from '../../../lib/botStats'
import { usePageLayout } from '../../../hooks/usePageLayout'

export default function BotHallScreen() {
  const router = useRouter()
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['bot-leaderboard'],
    queryFn: () => apiGet<BotStatsResponse>('/api/v1/leaderboard/bots?limit=100'),
  })
  const bots = mergeBotStats(data?.entries ?? [], data?.diceLuck)
  // Phones: one column, compact cards. Tablet/desktop: a 2-column grid with
  // the shared page gutters and larger card text, since each card has room.
  const layout = usePageLayout({ columns: { tablet: 2, desktop: 2 }, gap: 12 })
  const roomy = layout.tier !== 'phone'

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      <ScreenHeader title="Synthetic Command" icon="robot-outline" />

      {isLoading ? (
        <View className="flex-1 items-center justify-center">
          <ActivityIndicator color="#8aa14a" />
        </View>
      ) : error ? (
        <View className="flex-1 items-center justify-center gap-3 px-6">
          <Text className="font-stencil text-ink uppercase">Failed to load bot records</Text>
          <TouchableOpacity
            onPress={() => { void refetch() }}
            className="bg-paper-up border border-olive rounded-md px-4 py-2"
            accessibilityLabel="Retry"
            accessibilityRole="button"
          >
            <Text className="font-stencil text-ink">Retry</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <FlatList
          // numColumns cannot change on a mounted list; remount when it does.
          key={`bots-${layout.columns}`}
          data={bots}
          keyExtractor={(bot) => bot.key}
          numColumns={layout.columns}
          columnWrapperStyle={layout.columns > 1 ? { gap: layout.gap } : undefined}
          contentContainerStyle={{ paddingHorizontal: layout.inset, paddingVertical: 12, gap: layout.columns > 1 ? layout.gap : 10 }}
          ListHeaderComponent={
            <Text className={`font-typed text-ink-soft ${roomy ? 'text-base' : 'text-sm'} mb-1`}>
              Select a commander to see their strategy, record, and story.
            </Text>
          }
          renderItem={({ item: bot }) => (
            <TouchableOpacity
              onPress={() => router.push(`/(app)/bots/${bot.key}`)}
              accessibilityLabel={`View ${bot.displayName} bot profile`}
              accessibilityRole="button"
              testID={`bot-card-${bot.key}`}
              style={layout.columns > 1 ? { width: layout.itemWidth } : undefined}
              className={`flex-row items-center bg-paper border-2 border-olive rounded-xl gap-3 ${roomy ? 'p-4' : 'p-3'}`}
            >
              <BotAvatar profile={bot.key} size={roomy ? 76 : 64} />
              <View className="flex-1 min-w-0">
                <Text className={`font-stencil text-ink uppercase ${roomy ? 'text-lg' : 'text-base'}`}>{bot.displayName}</Text>
                <Text className={`font-typed text-ink-soft mt-1 ${roomy ? 'text-sm' : 'text-xs'}`} numberOfLines={2}>
                  {bot.description}
                </Text>
                {bot.stats || bot.diceLuck ? (
                  <Text className={`font-typed text-olive-up mt-2 ${roomy ? 'text-xs' : 'text-[10px]'}`} numberOfLines={2}>
                    {formatBotCardStats(bot.stats, bot.diceLuck)}
                  </Text>
                ) : (
                  <Text className={`font-typed text-ink-soft mt-2 ${roomy ? 'text-xs' : 'text-[10px]'}`}>
                    // no completed games yet
                  </Text>
                )}
              </View>
              <Text className={`font-stencil text-blood ${roomy ? 'text-lg' : 'text-base'}`}>#{bot.displayRank}</Text>
            </TouchableOpacity>
          )}
        />
      )}
    </SafeAreaView>
  )
}
